import type { ReactNode } from 'react';
import { CURRENCY_CATALOGUE } from '@forecastify/shared';
import { Select } from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ U-13 · setup step 2's currency — the workspace's default, made with it.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The settings dialog's twelve (`CURRENCY_CATALOGUE`), and a plain list.** U-14 keeps search
 * for pickers over records; twelve fixed currencies is a short vocabulary, and the server refuses a
 * code that is not on it (`setupCurrencySchema`).
 *
 * ⚠️ **HUF for a Hungarian browser, EUR for anybody else.** The same reasoning as the time zone's
 * default — the browser's own answer where it has one — and the guess is one click from right.
 */
export function defaultCurrency(): string {
  const languages = typeof navigator === 'undefined' ? [] : navigator.languages;
  return languages.some((language) => language.toLowerCase().startsWith('hu')) ? 'HUF' : 'EUR';
}

/** A currency's name in the reader's language — one literal key per code, for the key check. */
function currencyName(code: string, t: Translate): string {
  switch (code) {
    case 'HUF':
      return t('setup.currency.HUF');
    case 'EUR':
      return t('setup.currency.EUR');
    case 'USD':
      return t('setup.currency.USD');
    case 'GBP':
      return t('setup.currency.GBP');
    case 'CHF':
      return t('setup.currency.CHF');
    case 'RON':
      return t('setup.currency.RON');
    case 'PLN':
      return t('setup.currency.PLN');
    case 'CZK':
      return t('setup.currency.CZK');
    case 'SEK':
      return t('setup.currency.SEK');
    case 'NOK':
      return t('setup.currency.NOK');
    case 'DKK':
      return t('setup.currency.DKK');
    case 'JPY':
      return t('setup.currency.JPY');
    default:
      return code;
  }
}

export function CurrencyField({
  value,
  onChange,
}: {
  readonly value: string;
  readonly onChange: (code: string) => void;
}): ReactNode {
  const t = useT();
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-muted-foreground text-[12px] font-medium">
        {t('setup.workspace.currency')}
      </span>
      <Select
        aria-label={t('setup.workspace.currency')}
        value={value}
        options={CURRENCY_CATALOGUE.map((entry) => ({
          value: entry.code,
          label: `${currencyName(entry.code, t)} (${entry.code})`,
          hint: entry.symbol,
        }))}
        onChange={(event) => {
          const found = CURRENCY_CATALOGUE.find((entry) => entry.code === event.target.value);
          if (found !== undefined) onChange(found.code);
        }}
      />
      <span className="text-muted-foreground text-[12px]">{t('setup.workspace.currencyHint')}</span>
    </div>
  );
}
